"use client";

import React, { useCallback, useRef, useState } from "react";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { animate, useMotionValue } from "framer-motion";
import { useSnackbar } from "notistack";
import { getAuth } from "@/src/core/auth/session";
import type { IListing } from "@/src/shared/types/listing";
import { type IFilter, GuestsModal } from "@/src/shared/features/search";
import { useListingPrice } from "@/src/shared/features/listing";
import DatePicker from "@/src/shared/components/DatePicker";
import StayDatesFooter from "@/src/shared/components/StayDatesFooter";
import useDateDifference from "@/src/shared/hooks/useDateDifference";
import { formatYearDate } from "@/src/shared/utils/timeFunctionsModel";
import type { BlockedDate } from "@/src/shared/types/blockedDate";
import useAnalytics from "@/src/lib/useAnalytics";
import { cn } from "@/src/lib/utils";
import { IOS_PUSH } from "@/src/shared/constants/animation";
import { useBookingRequest } from "../hooks/useBookingRequest";
import { listingService } from "../services";
import PushedPage from "./PushedPage";

interface IMessageHostFormProps {
  listing: IListing;
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  /** "12–15 Oct", or null before dates are picked. */
  datesLabel: string | null;
  handleUpdateQuery: () => void;
  blockedDates: BlockedDate[];
  checkoutOnlyDates: string[];
}

/**
 * The app's Message host screen (`MessageHostPage`): the listing, the stay's dates, guests
 * and price (each tap to change), the prompt and the message. "Send message" opens the
 * conversation with the host (a booking request, as before).
 */
const MessageHostForm: React.FC<IMessageHostFormProps> = ({
  listing,
  filters,
  setFilters,
  datesLabel,
  handleUpdateQuery,
  blockedDates,
  checkoutOnlyDates,
}) => {
  const router = useAppRouter();
  const { enqueueSnackbar } = useSnackbar();
  const analytics = useAnalytics();
  const requestBooking = useBookingRequest();
  const { user_profile } = getAuth();
  const [message, setMessage] = useState(
    (router.query?.message as string) ?? "",
  );
  // Shown on the message box itself when Send is pressed without a message.
  const [messageError, setMessageError] = useState<string | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  // The date and guest pickers are pages pushed over this one, as the app's screens.
  const [picker, setPicker] = useState<"dates" | "guests" | null>(null);
  const pickerProgress = useMotionValue(1);
  const openPicker = (which: "dates" | "guests") => {
    pickerProgress.set(1);
    setPicker(which);
    void animate(pickerProgress, 0, IOS_PUSH);
  };
  const closePicker = (then?: () => void) =>
    void animate(pickerProgress, 1, IOS_PUSH).then(() => {
      setPicker(null);
      then?.();
    });
  const [isSending, setIsSending] = useState(false);
  const nights = useDateDifference({
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
  });
  const { loading: priceLoading, data: price } = useListingPrice(filters);
  const total = price?.average_price ? price.average_price * nights : 0;

  const hostName = listing.host?.first_name ?? "";
  const photo = listing.images?.[0]?.url;
  const propertyLine = listingService.propertyTypeLabel(
    listing.property_type?.name ?? "",
    listing.place_type ?? "",
  );
  const max = {
    adults: listing.max_guest,
    children: listing.max_child,
    infants: listing.max_infant,
  };

  const updateGuestCount = useCallback(
    (type: keyof IFilter["guests"], op: "add" | "subtract") => {
      setFilters((f) => {
        const current = f.guests[type] ?? 0;
        const min = type === "adults" ? 1 : 0;
        const next = op === "add" ? current + 1 : Math.max(current - 1, min);
        return { ...f, guests: { ...f.guests, [type]: next } };
      });
    },
    [setFilters],
  );

  const send = async () => {
    if (isSending) return;
    if (!message.trim()) {
      setMessageError("Please write a message first");
      messageRef.current?.focus();
      return;
    }
    if (!filters.checkIn || !filters.checkOut) {
      openPicker("dates");
      return;
    }
    if (!listingService.guestsFit(filters.guests, max)) {
      enqueueSnackbar("Please change your guests to fit this place", {
        variant: "error",
      });
      openPicker("guests");
      return;
    }
    if (!user_profile) {
      enqueueSnackbar("You need to be logged in to contact the host.", {
        variant: "error",
      });
      return;
    }

    const payload = {
      message,
      from: formatYearDate(filters.checkIn),
      to: formatYearDate(filters.checkOut),
      listing_id: listing.id,
      guests: Number(filters.guests.adults),
      child: Number(filters.guests.children),
      infant: Number(filters.guests.infants),
      os_platform: 3,
    };
    try {
      analytics.add_to_cart({
        item: listing,
        value: listing.price,
        currency: "BDT",
        params: {
          booking_dates: `${payload.from} to ${payload.to}`,
          guests: payload.guests + payload.child + payload.infant,
        },
      });
    } catch (_error) {}

    setIsSending(true);
    await requestBooking(payload, {
      hostId: listing.host.id,
      guestId: user_profile.id,
      instant: false,
    });
    setIsSending(false);
  };

  const row = (label: string, value: React.ReactNode, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between py-4 text-left"
    >
      <span className="text-16 font-medium leading-5 text-ink">{label}</span>
      {value}
    </button>
  );
  const link = "text-16 leading-6 text-ink underline";

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-6">
        <div className="flex items-start gap-3 pb-6 pt-4">
          <div className="min-w-0 flex-1">
            <p className="text-16 leading-6 text-ink">{listing.title}</p>
            {propertyLine && (
              <p className="mt-1 text-14 leading-4 text-ink-secondary">
                {propertyLine}
              </p>
            )}
          </div>
          {photo && (
            <img
              src={photo}
              alt=""
              className="h-16 w-16 shrink-0 rounded-xl object-cover"
            />
          )}
        </div>

        <hr className="my-1 border-[#EBEBEB]" />
        {row(
          "Dates",
          <span className={cn(link, "font-medium")}>
            {datesLabel ?? "Add dates"}
          </span>,
          () => openPicker("dates"),
        )}

        <hr className="my-1 border-[#EBEBEB]" />
        {row(
          "Guests",
          <span className={cn(link, "font-medium")}>
            {listingService.guestsLabel(filters.guests)}
          </span>,
          () => openPicker("guests"),
        )}

        {!priceLoading && total > 0 && (
          <>
            <hr className="my-1 border-[#EBEBEB]" />
            <div className="flex items-center justify-between py-4">
              <span className="text-16 font-medium leading-5 text-ink">
                Price
              </span>
              <span className={link}>৳{total.toLocaleString("en-US")}</span>
            </div>
          </>
        )}

        <p className="mt-1 text-16 leading-5 text-ink">
          {listingService.messagePrompt(hostName)}
        </p>
        <textarea
          ref={messageRef}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (messageError) setMessageError(null);
          }}
          placeholder="Write your message here..."
          aria-label="Message to the host"
          aria-invalid={!!messageError}
          aria-describedby={messageError ? "message-host-error" : undefined}
          className={cn(
            "mt-4 h-[140px] w-full resize-none rounded-lg border p-4 text-16 font-medium leading-5 text-ink outline-none placeholder:text-ink-secondary",
            messageError
              ? "border-danger focus:border-danger"
              : message.trim()
                ? "border-ink focus:border-ink"
                : "border-[#DDDDDD] focus:border-ink",
            !messageError && "mb-6",
          )}
        />
        {messageError && (
          <p
            id="message-host-error"
            role="alert"
            className="mb-6 mt-2 text-14 text-danger"
          >
            {messageError}
          </p>
        )}
      </div>

      <div className="border-t border-[#EBEBEB] px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={() => void send()}
          disabled={isSending}
          className="flex h-14 w-full items-center justify-center rounded-xl bg-ink text-16 font-semibold text-white active:opacity-80 disabled:opacity-70"
        >
          {isSending ? (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            "Send message"
          )}
        </button>
      </div>

      {picker === "dates" && (
        <PushedPage
          title="Select Dates"
          progress={pickerProgress}
          onBack={() => closePicker()}
          onSwipedBack={() => setPicker(null)}
          // The app's date screen: check-in | days | check-out over a pink "Done".
          footerClassName="border-[#C3C6D3] pt-4"
          footer={
            <StayDatesFooter
              checkIn={filters.checkIn}
              checkOut={filters.checkOut}
              onDone={() => closePicker(handleUpdateQuery)}
            />
          }
        >
          <DatePicker
            appearance="app"
            containerClass="px-2"
            selectedDates={{
              checkIn: filters.checkIn,
              checkOut: filters.checkOut,
            }}
            onDateChange={(dates) => setFilters((f) => ({ ...f, ...dates }))}
            onConfirm={() => closePicker(handleUpdateQuery)}
            isDetailsPage={true}
            isMonthSingleView={true}
            blockedDates={blockedDates}
            checkoutOnlyDates={checkoutOnlyDates}
          />
        </PushedPage>
      )}
      {picker === "guests" && (
        <PushedPage
          title="Guests"
          progress={pickerProgress}
          onBack={() => closePicker()}
          onSwipedBack={() => setPicker(null)}
          footer={
            <button
              type="button"
              onClick={() => closePicker()}
              className="flex h-14 w-full items-center justify-center rounded-xl bg-ink text-16 font-semibold text-white active:opacity-80"
            >
              Done
            </button>
          }
        >
          <GuestsModal
            isAppliedValidation={true}
            maxAdults={listing.max_guest}
            maxChildren={listing.max_child}
            maxInfants={listing.max_infant}
            filters={filters}
            updateGuestCount={updateGuestCount}
          />
        </PushedPage>
      )}
    </>
  );
};

export default MessageHostForm;
